import { ItemId } from 'kuaidong-model'
import { KDMessageBox } from 'kuaidong-ui'
import { AppUtil, createIPC } from 'kuaidong-lib'
import { IActivityData } from '../models/config'
import * as rewardService from './rewardService'
import * as rewardTransService from './rewardTransService'
import { IRewardPopupRewards } from '../models/reward'
import { ILotteryResult, LotteryGlobalAction } from '../models/lottery'

import ImgRewardsPopupTitle from '../images/img_rewards_popup_title@2x.png'
import ImgRewardsPopupBtnOk from '../images/img_rewards_popup_btn_ok@2x.png'
import ImgLotteryRewardTagR from '../images/img_lottery_reward_tag_r@2x.png'
import ImgLotteryRewardTagSr from '../images/img_lottery_reward_tag_sr@2x.png'
import ImgLotteryRewardTagUr from '../images/img_lottery_reward_tag_ur@2x.png'
import ImgRewardsPopupBtnOk2 from '../images/img_rewards_popup_btn_ok2@2x.png'
import ImgLotteryRewardTagSsr from '../images/img_lottery_reward_tag_ssr@2x.png'
import ImgBtnJumpToSplusAgreement from '../images/img_btn_jump_to_splus_agreement@2x.png'

/** RewardsPopup：稀有度角标复用 `icon` + 该 class，左上角定位见 RewardsPopup.vue */
const lotteryRarityTagIconClass = 'lottery-rarity-tag'

/** 我的奖品列表：`IReward.data` 上存放角标 URL 的 key */
export const lotteryRewardListDataKey = 'lotteryTag'

/** 抽奖稀有度角标 UR → SSR → SR → R，按 `unfinished_pic` 分组顺序映射 */
export const lotteryRewardTags = [
  ImgLotteryRewardTagUr,
  ImgLotteryRewardTagSsr,
  ImgLotteryRewardTagSr,
  ImgLotteryRewardTagR,
]

export interface ILotteryRewardStage {
  stage_id?: string
  unfinished_pic?: string
  reward_items?: Array<{ item_pic?: string }>
  reward_small_pic?: string
}

export function getLotteryRewardItemPic(_reward?: ILotteryRewardStage | null) {
  return _reward?.reward_items?.[0]?.item_pic || _reward?.reward_small_pic || ''
}

export function buildLotteryRewardTagMap(_rewards: ILotteryRewardStage[], _tagImages: string[]) {
  const groups = new Map<string, number>()

  _rewards.forEach((_reward) => {
    const key = _reward.unfinished_pic || ''
    const no = +((_reward.stage_id || '').replace(/\D+/g, '') || 0)

    if (!key) {
      return
    }

    if (!groups.has(key) || no < groups.get(key)!) {
      groups.set(key, no)
    }
  })

  const map: Record<string, string> = {}

  ;[...groups.entries()]
    .sort((_a, _b) => _a[1] - _b[1])
    .forEach(([_unfinishedPic], _idx) => {
      map[_unfinishedPic] = _tagImages[_idx] || _tagImages[_tagImages.length - 1] || ''
    })

  return map
}

function getLotteryRewardTag(_reward?: ILotteryRewardStage | null, _tagMap?: Record<string, string>) {
  if (!_reward?.unfinished_pic || !_tagMap) {
    return ''
  }

  return _tagMap[_reward.unfinished_pic] || ''
}

function buildLotteryRewardDisplay(_reward?: ILotteryRewardStage | null, _tagMap?: Record<string, string>) {
  return {
    image: getLotteryRewardItemPic(_reward),
    icon: getLotteryRewardTag(_reward, _tagMap),
  }
}

export function appendLotteryRewardDisplay<T extends ILotteryRewardStage>(
  _rewards: T[],
  _tagMap: Record<string, string>
) {
  return _rewards.map((_reward) => ({
    ..._reward,
    ...buildLotteryRewardDisplay(_reward, _tagMap),
  }))
}

export function buildLotteryRewardPopupItems(
  _rewards: IRewardPopupRewards['rewards'],
  _tagMap: Record<string, string>
) {
  return _rewards.map((_reward) => ({
    ..._reward,
    image: getLotteryRewardItemPic(_reward.data),
    icon: getLotteryRewardTag(_reward.data, _tagMap),
    iconClass: lotteryRarityTagIconClass,
  }))
}

export function buildLotteryRewardListFields(
  _reward?: ILotteryRewardStage | null,
  _tagMap?: Record<string, string>,
  _data?: Record<string, unknown>
) {
  const display = buildLotteryRewardDisplay(_reward, _tagMap)

  return {
    icon: display.image,
    data: {
      ...(_data || {}),
      ...(display.icon ? { [lotteryRewardListDataKey]: display.icon } : {}),
    },
  }
}

/** 角标相对奖励图尺寸（设计稿 44×25 @ 100px 宽） */
export function buildLotteryRewardTagSize(_size: number) {
  return {
    width: Math.round((_size * 44) / 100),
    height: Math.round((_size * 25) / 100),
  }
}

export function buildLotteryRewards(
  _data: IActivityData | null,
  _prefix = 'stage_gacha_',
  _options?: { tagImages?: string[] }
) {
  const rewards = rewardService.buildRewards(_data).filter((_reward) => _reward.stage_id.startsWith(_prefix))

  if (!_options?.tagImages?.length) {
    return rewards
  }

  const tagMap = buildLotteryRewardTagMap(rewards, _options.tagImages)

  return appendLotteryRewardDisplay(rewards, tagMap)
}

export function buildLotteryRewardsFromResult(
  _lotteryResult: ILotteryResult,
  _data: IActivityData | null,
  _params?: {
    ipc?: ReturnType<typeof createIPC> | null

    title?: {
      image: string
      label: string
      style?: Record<string, string | undefined>
      width: number
      height: number
    }

    btnOk?: {
      image: string
      label: string
      width: number
      height: number
      action?: () => void
    }

    /** 奖池 stage 列表，与 lotteryRewardTags 一起传入时启用新奖池展示 */
    lotteryPoolRewards?: ILotteryRewardStage[]
    lotteryRewardTags?: string[]
  }
) {
  const rewardMap = rewardService.buildRewardMap(_data)

  const hasMulti = _lotteryResult.reward_list.length > 1
  const hasTrans = _lotteryResult.reward_list.some((_reward) => _reward.trans_flag)
  const hasSplus = _lotteryResult.reward_list.some((_reward) => _reward.member_pop)

  let result = rewardService.buildRewardPopupRewards(
    _lotteryResult.reward_list.map((_reward) => rewardMap[_reward.stage_id]),
    {
      title: {
        image: ImgRewardsPopupTitle,
        label: '哇！恭喜你获得',
        width: 375,
        height: 122,
        ...(_params?.title || {}),
      },

      btnOk: {
        ...(hasSplus
          ? {
              image: ImgRewardsPopupBtnOk2,
              label: '前往领取',
              width: 194,
              height: 60,
              action: () => openSplus3DaysFreeSKUMessgeBox(_params?.ipc),
            }
          : {
              image: ImgRewardsPopupBtnOk,
              label: '开心收下',
              width: 194,
              height: 60,
              action: () => {},
            }),
        ...(_params?.btnOk || {}),
      },
    }
  )

  if (_params?.lotteryPoolRewards?.length) {
    const tagMap = buildLotteryRewardTagMap(
      _params.lotteryPoolRewards,
      _params.lotteryRewardTags?.length ? _params.lotteryRewardTags : lotteryRewardTags
    )

    result = {
      ...result,
      rewards: buildLotteryRewardPopupItems(result.rewards, tagMap),
    }
  }

  if (hasTrans) {
    const useRewardTrans = !!_params?.lotteryPoolRewards?.length

    result = {
      ...result,

      text: hasMulti
        ? ''
        : `你已拥有「${result.rewards[0]?.name}」，奖励转换为${result.rewards[0]?.data.finished_pic || '扭蛋瓶'}`,

      rewards: result.rewards.map((_reward, _idx) => {
        if (!_lotteryResult.reward_list[_idx].trans_flag) {
          return _reward
        }

        if (useRewardTrans) {
          return {
            ..._reward,
            ...rewardTransService.buildRewardTransItem({
              rewardIcon: getLotteryRewardItemPic(_reward.data),
              stage: _reward.data,
              transCount: _lotteryResult.reward_list[_idx].trans_count,
            }),
          }
        }

        return {
          ..._reward,
          name: `${_reward.data?.finished_pic || '扭蛋瓶'}*${_reward.data.aim_count}`,
          image: _reward.data?.unfinished_pic,
        }
      }),
    }
  }

  return result
}

export function openSplus3DaysFreeSKUMessgeBox(_ipc?: ReturnType<typeof createIPC> | null) {
  KDMessageBox.confirm('开通后您将免费体验3天S+会员，并解锁会员专属奖励！', '是否确认开通领取', {
    showCloseIcon: true,
    cancelButtonType: 'secondary',
    cancelButtonText: '忍痛放弃',
    confirmButtonType: 'danger',
    confirmButtonText: '同意并开通',
    additionalButton: {
      on: () =>
        AppUtil.jumpUrlNewTab(
          'https://www.tiantiantiaosheng.com/app/img2?cfg=9c8481e0-9546-4408-8758-57b707d4ed78.json',
          '《会员自动续费协议》'
        ),
      text: '试用结束后将按38/季订阅扣费，可随时取消开通则表示你已阅读并同意《会员自动续费协议》',
      image: ImgBtnJumpToSplusAgreement,
      width: 215,
      showOnCard: true,
      disableCloseOnClick: true,
    },
  }).then((_res) => {
    if (_res) {
      return _ipc?.send(LotteryGlobalAction.Purchase, ItemId.SplusRenewQuarter3)
    }
  })
}

export function useLottery(_data: IActivityData | null, _prefix = 'stage_gacha_') {
  return {
    buildLotteryRewards,
    buildLotteryRewardsFromResult,
    openSplus3DaysFreeSKUMessgeBox,
  }
}
